import { DEFAULT_TIMEZONE } from '../../../../constants';
import { DateTime } from 'luxon';

/** Arguments for {@link canDeleteVisit}. */
export type CanDeleteVisitArgs = {
  /** Visit start time in epoch milliseconds. */
  visitStartTimeMs: number;
  /**
   * Current time in epoch milliseconds. Callers should pass
   * `TimeFacade.now()` (API) or `DateTime.now().toMillis()` (portal).
   */
  nowMs: number;
  /** True when the acting user created the visit. */
  isCreator: boolean;
  /**
   * True when the acting user may delete another user's visit in the current
   * month (e.g. has `CAN_AUDIT_MEMBER_STATUS_CHANGE_REQUESTS` on the member).
   */
  canElevatedDelete: boolean;
};

/**
 * Whether a home visit may be deleted under Care Portal rules.
 *
 * Creators may delete visits from the same calendar day; elevated deleters
 * (managers/admins) may delete visits from the same calendar month. Both
 * require the visit to be in the same calendar year as `nowMs`.
 *
 * Calendar day/month/year are evaluated in {@link DEFAULT_TIMEZONE}
 * (`America/Chicago`) so API and Care Portal agree regardless of host TZ.
 *
 * @param args Visit time, clock, and permission flags.
 * @returns True when delete is allowed.
 */
export function canDeleteVisit({
  visitStartTimeMs,
  nowMs,
  isCreator,
  canElevatedDelete,
}: CanDeleteVisitArgs): boolean {
  const visitTime = DateTime.fromMillis(visitStartTimeMs, {
    zone: DEFAULT_TIMEZONE,
  });
  const now = DateTime.fromMillis(nowMs, { zone: DEFAULT_TIMEZONE });

  if (!visitTime.isValid || !now.isValid) {
    return false;
  }

  if (visitTime.year !== now.year) {
    return false;
  }

  if (isCreator && visitTime.hasSame(now, 'day')) {
    return true;
  }

  if (canElevatedDelete && visitTime.hasSame(now, 'month')) {
    return true;
  }

  return false;
}
